import React, { useState, useEffect, useCallback, useMemo } from 'react';
import LexAIService from '../services/api';
import { DashboardClient } from '../types/api';

interface ClientsListProps {
  onClientSelect?: (client: DashboardClient) => void;
}

/**
 * ClientsList component displays all people in the sandbox with Mercury sync functionality.
 * Features:
 * - List of all individual people from client.md files
 * - Sync buttons per client folder (syncs all people in that folder)
 * - "Sync All" button to populate all client folders with Mercury data
 * - Progress tracking for batch operations
 */
const ClientsList: React.FC<ClientsListProps> = ({ onClientSelect }) => {
  const [people, setPeople] = useState<DashboardClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null); // Track which folder is syncing
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{ current: number; total: number; clientName: string } | null>(null);
  const [syncResults, setSyncResults] = useState<Map<string, { success: boolean; message: string }>>(new Map());

  // Get unique folders for sync operations
  const uniqueFolders = useMemo(() => {
    const folders = new Set<string>();
    people.forEach(p => {
      if (p.clientFolder) folders.add(p.clientFolder);
    });
    return Array.from(folders);
  }, [people]);

  // Load people on mount
  useEffect(() => {
    loadPeople();
  }, []);

  const loadPeople = async () => {
    setLoading(true);
    try {
      const peopleData = await LexAIService.getSandboxPeople();
      setPeople(peopleData);
    } catch (error) {
      console.error('Error loading people:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sync a single folder with Mercury
  const handleSyncFolder = useCallback(async (folderName: string) => {
    setSyncing(folderName);
    setSyncResults(prev => {
      const newMap = new Map(prev);
      newMap.delete(folderName);
      return newMap;
    });

    try {
      const result = await LexAIService.syncClientWithMercury(folderName, true);
      setSyncResults(prev => {
        const newMap = new Map(prev);
        newMap.set(folderName, result);
        return newMap;
      });

      // Reload people to get updated data
      if (result.success) {
        await loadPeople();
        // Notify sidebar to refresh its cached client data
        window.dispatchEvent(new CustomEvent('lexai-sync-complete', { detail: { tab: 'clients' } }));
      }
    } catch (error) {
      setSyncResults(prev => {
        const newMap = new Map(prev);
        newMap.set(folderName, {
          success: false,
          message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
        });
        return newMap;
      });
    } finally {
      setSyncing(null);
    }
  }, []);

  // Sync all folders with Mercury
  const handleSyncAll = useCallback(async () => {
    if (uniqueFolders.length === 0) return;

    setSyncingAll(true);
    setSyncResults(new Map());

    try {
      const results = await LexAIService.syncAllClientsWithMercury(
        uniqueFolders,
        (current, total, clientName) => {
          setSyncProgress({ current, total, clientName });
        }
      );

      // Update results map
      const newResultsMap = new Map<string, { success: boolean; message: string }>();
      results.forEach(r => {
        newResultsMap.set(r.clientName, { success: r.success, message: r.message });
      });
      setSyncResults(newResultsMap);

      // Reload people to get updated data
      await loadPeople();
      // Notify sidebar to refresh its cached client data
      window.dispatchEvent(new CustomEvent('lexai-sync-complete', { detail: { tab: 'clients' } }));
    } catch (error) {
      console.error('Error syncing all folders:', error);
    } finally {
      setSyncingAll(false);
      setSyncProgress(null);
    }
  }, [uniqueFolders]);

  // Group people by folder for display (must be before conditional returns for hooks rules)
  const peopleByFolder = useMemo(() => {
    const grouped = new Map<string, DashboardClient[]>();
    people.forEach(person => {
      const folder = person.clientFolder || 'Unknown';
      if (!grouped.has(folder)) {
        grouped.set(folder, []);
      }
      grouped.get(folder)!.push(person);
    });
    return grouped;
  }, [people]);

  if (loading) {
    return (
      <div className="clients-list-container" style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="loading-dots">
          <div className="loading-dot"></div>
          <div className="loading-dot"></div>
          <div className="loading-dot"></div>
        </div>
        <p style={{ color: 'var(--obsidian-text-secondary)', marginTop: '1rem' }}>Loading people...</p>
      </div>
    );
  }

  return (
    <div className="clients-list-container" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Sync All button */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid var(--obsidian-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'var(--obsidian-bg-secondary)'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--obsidian-text-primary)' }}>
            All Clients
          </h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: 'var(--obsidian-text-secondary)' }}>
            {people.length} {people.length === 1 ? 'person' : 'people'} in {uniqueFolders.length} {uniqueFolders.length === 1 ? 'folder' : 'folders'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={loadPeople}
            disabled={loading || syncingAll}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: 'var(--obsidian-bg)',
              border: '1px solid var(--obsidian-border)',
              borderRadius: '6px',
              color: 'var(--obsidian-text-primary)',
              cursor: loading || syncingAll ? 'not-allowed' : 'pointer',
              opacity: loading || syncingAll ? 0.6 : 1,
              fontSize: '0.875rem',
              fontWeight: 500
            }}
            title="Refresh list"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>refresh</span>
            Refresh
          </button>
          <button
            onClick={handleSyncAll}
            disabled={syncingAll || uniqueFolders.length === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: syncingAll ? 'var(--obsidian-accent-muted)' : 'var(--obsidian-accent)',
              border: 'none',
              borderRadius: '6px',
              color: 'white',
              cursor: syncingAll || uniqueFolders.length === 0 ? 'not-allowed' : 'pointer',
              opacity: uniqueFolders.length === 0 ? 0.6 : 1,
              fontSize: '0.875rem',
              fontWeight: 500
            }}
            title="Sync all folders with Mercury CRM data"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
              {syncingAll ? 'sync' : 'cloud_sync'}
            </span>
            {syncingAll ? 'Syncing...' : 'Populate All with Mercury'}
          </button>
        </div>
      </div>

      {/* Progress bar for batch sync */}
      {syncingAll && syncProgress && (
        <div style={{
          padding: '12px 20px',
          backgroundColor: 'var(--obsidian-accent-bg)',
          borderBottom: '1px solid var(--obsidian-border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--obsidian-text-primary)' }}>
              Syncing: {syncProgress.clientName}
            </span>
            <span style={{ fontSize: '0.875rem', color: 'var(--obsidian-text-secondary)' }}>
              {syncProgress.current} / {syncProgress.total}
            </span>
          </div>
          <div style={{
            height: '4px',
            backgroundColor: 'var(--obsidian-border)',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${(syncProgress.current / syncProgress.total) * 100}%`,
              backgroundColor: 'var(--obsidian-accent)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      )}

      {/* People list grouped by folder */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
        {people.length === 0 ? (
          <div style={{
            padding: '3rem 2rem',
            textAlign: 'center',
            color: 'var(--obsidian-text-secondary)'
          }}>
            <span className="material-symbols-outlined" style={{
              fontSize: '4rem',
              color: 'var(--obsidian-text-disabled)',
              marginBottom: '1rem',
              display: 'block'
            }}>
              person_off
            </span>
            <h3 style={{ color: 'var(--obsidian-text-primary)', marginBottom: '0.5rem' }}>
              No People Found
            </h3>
            <p style={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
              Create client folders in the LexAI-Sandbox SharePoint folder,<br />
              then click "Populate All with Mercury" to sync data.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {Array.from(peopleByFolder.entries()).map(([folder, folderPeople]) => {
              const syncResult = syncResults.get(folder);
              const isCurrentlySyncing = syncing === folder;
              const hasSyncedData = folderPeople.some(p => p.hasSyncedData);

              return (
                <div key={folder}>
                  {/* Folder header with sync button */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                    padding: '8px 12px',
                    backgroundColor: 'var(--obsidian-bg-secondary)',
                    borderRadius: '6px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '18px', color: 'var(--obsidian-text-secondary)' }}>
                        folder
                      </span>
                      <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--obsidian-text-primary)' }}>
                        {folder}
                      </span>
                      {hasSyncedData && (
                        <span style={{
                          fontSize: '0.75rem',
                          padding: '2px 6px',
                          backgroundColor: 'var(--success-color, #22c55e)',
                          color: 'white',
                          borderRadius: '4px'
                        }}>
                          Synced
                        </span>
                      )}
                      {syncResult && (
                        <span style={{
                          fontSize: '0.75rem',
                          color: syncResult.success ? 'var(--success-color, #22c55e)' : 'var(--error-color, #ef4444)'
                        }}>
                          {syncResult.success ? '✓ Updated' : '✗ Failed'}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSyncFolder(folder);
                      }}
                      disabled={isCurrentlySyncing || syncingAll}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        backgroundColor: 'transparent',
                        border: '1px solid var(--obsidian-border)',
                        borderRadius: '4px',
                        color: 'var(--obsidian-text-secondary)',
                        cursor: isCurrentlySyncing || syncingAll ? 'not-allowed' : 'pointer',
                        opacity: syncingAll ? 0.5 : 1,
                        fontSize: '0.75rem'
                      }}
                      title={`Sync ${folder} with Mercury CRM`}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{
                          fontSize: '14px',
                          animation: isCurrentlySyncing ? 'spin 1s linear infinite' : 'none'
                        }}
                      >
                        {isCurrentlySyncing ? 'sync' : 'cloud_download'}
                      </span>
                      {isCurrentlySyncing ? 'Syncing...' : 'Sync'}
                    </button>
                  </div>

                  {/* People in this folder */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingLeft: '8px' }}>
                    {folderPeople.map((person) => (
                      <div
                        key={person.id}
                        style={{
                          padding: '10px 14px',
                          backgroundColor: 'var(--obsidian-bg)',
                          border: '1px solid var(--obsidian-border)',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--obsidian-hover-bg)';
                          e.currentTarget.style.borderColor = 'var(--obsidian-accent)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--obsidian-bg)';
                          e.currentTarget.style.borderColor = 'var(--obsidian-border)';
                        }}
                        onClick={() => onClientSelect?.(person)}
                      >
                        <span className="material-symbols-outlined" style={{
                          fontSize: '20px',
                          color: person.hasSyncedData ? 'var(--obsidian-accent)' : 'var(--obsidian-text-disabled)',
                          padding: '6px',
                          backgroundColor: 'var(--obsidian-accent-bg)',
                          borderRadius: '6px'
                        }}>
                          person
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontWeight: 500,
                            color: 'var(--obsidian-text-primary)',
                            fontSize: '0.875rem'
                          }}>
                            {person.name || person.preferredName}
                          </div>
                          <div style={{
                            fontSize: '0.75rem',
                            color: 'var(--obsidian-text-secondary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            flexWrap: 'wrap'
                          }}>
                            {person.email && <span>{person.email}</span>}
                            {person.phone && (
                              <>
                                {person.email && <span>•</span>}
                                <span>{person.phone}</span>
                              </>
                            )}
                            {person.deals && person.deals.length > 0 && (
                              <>
                                {(person.email || person.phone) && <span>•</span>}
                                <span>{person.deals.length} deal{person.deals.length !== 1 ? 's' : ''}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <span className="material-symbols-outlined" style={{
                          fontSize: '16px',
                          color: 'var(--obsidian-text-disabled)'
                        }}>
                          chevron_right
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sync animation keyframes */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ClientsList;
